import React, { useState, useEffect } from 'react';
import '../styles/WaitlistModal.css';

const WaitlistModal = ({ 
  onClose, 
  onSubmit, 
  preSelectedAgents = [], 
  isSubmitting = false,
  hasSubmitted = false
}) => {
  const [formData, setFormData] = useState({
    businessName: '',
    industry: '',
    topAgents: []
  });

  const [errors, setErrors] = useState({});

  // Update topAgents when preSelectedAgents changes
  useEffect(() => {
    if (preSelectedAgents.length > 0) {
      // Extract just the names of the agents
      const agentNames = preSelectedAgents.map(agent => agent.name);
      // Limit to 5 if more were selected
      const topFive = agentNames.slice(0, 5);
      
      setFormData(prevData => ({
        ...prevData,
        topAgents: topFive
      }));
    }
  }, [preSelectedAgents]);

  const agentOptions = [
    "AI Influencer Outreach Manager",
    "Instagram Content Creator",
    "LinkedIn Post Generator",
    "YouTube Video Script Creator",
    "TikTok Trend Analyzer",
    "Facebook Ads Manager",
    "Email Marketing Automator",
    "Customer Support Bot",
    "Lead Generation Assistant",
    "SEO Content Optimizer"
  ];

  // Add any pre-selected agents that aren't in the default options
  preSelectedAgents.forEach(agent => {
    if (!agentOptions.includes(agent.name)) {
      agentOptions.push(agent.name);
    }
  });

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.businessName) tempErrors.businessName = "Business name is required";
    if (!formData.industry) tempErrors.industry = "Industry is required";
    if (formData.topAgents.length === 0) tempErrors.topAgents = "Please select at least one agent";
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleAgentSelect = (agent) => {
    let updatedAgents = [...formData.topAgents];
    
    if (updatedAgents.includes(agent)) {
      updatedAgents = updatedAgents.filter(a => a !== agent);
    } else if (updatedAgents.length < 5) {
      updatedAgents.push(agent);
    }
    
    setFormData({
      ...formData,
      topAgents: updatedAgents
    });
    
    if (errors.topAgents) {
      setErrors({
        ...errors,
        topAgents: null
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Prevent submission if already submitted or currently submitting
    if (hasSubmitted || isSubmitting) return;
    
    if (validateForm()) {
      // If valid, submit the form
      onSubmit(formData);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button 
          className="close-button" 
          onClick={onClose} 
          disabled={isSubmitting}
        >
          &times;
        </button>
        
        <h2>Complete Your <span className="highlight">Free Access</span> Setup</h2>
        <p className="modal-subtitle">Tell us about your business to customize your experience</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="businessName">Business Name</label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              className={errors.businessName ? 'error' : ''}
            />
            {errors.businessName && <span className="error-message">{errors.businessName}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="industry">Your Industry</label>
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className={errors.industry ? 'error' : ''}
            >
              <option value="">Select your industry</option>
              <option value="E-commerce">E-commerce</option>
              <option value="SaaS">SaaS</option>
              <option value="Digital Products">Digital Products</option>
              <option value="Coaching/Consulting">Coaching/Consulting</option>
              <option value="Professional Services">Professional Services</option>
              <option value="Health & Wellness">Health & Wellness</option>
              <option value="Education">Education</option>
              <option value="Other">Other</option>
            </select>
            {errors.industry && <span className="error-message">{errors.industry}</span>}
          </div>
          
          <div className="form-group agent-selection">
            <label>Select Your Top 5 AI Agents {preSelectedAgents.length > 0 ? '(Pre-selected from your choices)' : ''}</label>
            <div className="agent-options">
              {agentOptions.map((agent, index) => (
                <div 
                  className={`agent-option ${formData.topAgents.includes(agent) ? 'selected' : ''}`}
                  key={index}
                  onClick={() => handleAgentSelect(agent)}
                >
                  <span className="agent-checkbox">
                    {formData.topAgents.includes(agent) ? '✓' : ''}
                  </span>
                  <span className="agent-name">{agent}</span>
                </div>
              ))}
            </div>
            {errors.topAgents && <span className="error-message">{errors.topAgents}</span>}
          </div>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting || hasSubmitted}
          >
            {isSubmitting ? 'Processing Your Request...' : 
             hasSubmitted ? 'Form Already Submitted' : 'Activate Free Access'}
          </button>
          
          {hasSubmitted && (
            <div className="success-message">
              Your information has been submitted! Please wait...
            </div>
          )}
          
          {isSubmitting && (
            <div className="loading-indicator">
              <div className="loading-spinner"></div>
              <p>Activating your AI agents...</p>
            </div>
          )}
          
          <p className="privacy-note">
            We respect your privacy and will never share your information with third parties.
          </p>
        </form>
      </div>
    </div>
  );
};

export default WaitlistModal;
